import React from "react";
import { Link } from "wouter";
import { useListCategories } from "@workspace/api-client-react";
import { useI18n } from "@/contexts/I18nContext";
import { ChevronRight } from "lucide-react";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { Badge } from "@/components/ui/badge";

export default function CategoriesPage() {
  const { t, language, isRTL } = useI18n();
  const { data: categories, isLoading } = useListCategories();

  return (
    <div className="flex-1 bg-muted/10 pb-20">
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">
        <div className="mb-12 text-center md:text-start">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">{t("All Categories Heading")}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto md:mx-0">
            {t("Browse USDT-friendly businesses in Dubai by category.")}
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories?.map((category, i) => {
              const Icon = getCategoryIcon(category.name);
              return (
                <Link key={category.id} href={`/explore?categoryId=${category.id}`} className="group block h-full" data-testid={`card-category-${category.id}`}>
                  <div
                    className="h-full bg-card hover:bg-card border border-border/60 hover:border-primary/50 rounded-3xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 relative overflow-hidden"
                    style={{ animationDelay: `${i * 30}ms` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="h-24 w-24 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 mb-6 shadow-sm ring-1 ring-primary/20 group-hover:ring-primary/50 relative z-10">
                      <Icon className="h-10 w-10" />
                    </div>

                    <h3 className="font-bold text-2xl mb-3 group-hover:text-primary transition-colors relative z-10">
                      {language === "ar" ? t(category.name as keyof typeof import("@/lib/i18n").translations.en) : category.name}
                    </h3>

                    <div className="mt-auto pt-4 relative z-10 w-full flex items-center justify-between">
                      <Badge variant="secondary" className="px-4 py-1.5 text-sm font-medium rounded-full bg-muted border-border/50 shadow-sm group-hover:bg-primary/10 group-hover:text-primary group-hover:border-primary/20 transition-colors">
                        {category.listingCount} {t("listings")}
                      </Badge>
                      <div className="h-8 w-8 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all">
                        <ChevronRight className={`h-4 w-4 ${isRTL ? "rtl-flip" : ""}`} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
